import { combineLatest, Observable, of, Subject, switchMap } from 'rxjs';
import { catchError, map, startWith, takeUntil, tap, throttleTime } from 'rxjs/operators';
import { EnvironmentInjector, Injectable, OnDestroy, runInInjectionContext } from '@angular/core';
import { FeatureNames } from '../../model/model';
import { AuthorizationService } from '@pure/authz-authorizer';
import { FeatureFlagService, FeatureFlagStatusResponse, Pure1NavigationService } from '@pure/pure1-ui-platform-angular';
import { DatadogRumConfigService } from 'core/src/app/datadog-rum-config.service';
import { ConditionExpr, MENU_SEPARATOR_TITLE, PathExpr, SubTabConfig, TabConfig } from './tabs.model';

export const PERMISSIONS_THROTTLE_MS = 1;
export const TAB_NOT_SHOWN: unique symbol = Symbol();

const isTabShown = <Tab>(tab: Tab | typeof TAB_NOT_SHOWN): tab is Tab => tab !== TAB_NOT_SHOWN;

interface ITabPath {
    navMenuId?: string;
    path?: string;
    href?: string;
    target?: string;
}

export interface ITab extends ITabPath {
    active?: boolean;
    title: string;
    subTabs?: ITab[];
}

export interface IMainTab extends ITabPath {
    active?: boolean;
    title: string;
    icon?: string;
    subTabs?: ITab[];
    submenu?: 'draas';
}

export interface Bag {
    isAllowedMap: Map<string, boolean>;
    isFeatureEnabledMap: FeatureFlagStatusResponse;
}

@Injectable({ providedIn: 'root' })
export class TabsService implements OnDestroy {
    private destroy$ = new Subject<void>();

    constructor(
        private featurePolicyService: FeatureFlagService,
        private authorizationService: AuthorizationService,
        private environmentInjector: EnvironmentInjector,
        private pure1NavigationService: Pure1NavigationService,
        private datadogRumConfigService: DatadogRumConfigService,
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    build$(menu: TabConfig[]): Observable<readonly IMainTab[]> {
        if (menu.length === 0) {
            return of([]);
        }

        /**
         * Collect declared permissions and feature names from the menu configuration.
         */
        const permissionSet = new Set<string>();
        const enabledFeatureNameSet = new Set<FeatureNames>();
        const disabledFeatureNameSet = new Set<FeatureNames>();

        function walkCondition(node?: ConditionExpr) {
            if (!node || typeof node === 'function') {
                return;
            } else if (node.check === 'isAllowed') {
                permissionSet.add(node.permission);
            } else if (node.check === 'featureEnabled') {
                enabledFeatureNameSet.add(node.featureName);
            } else if (node.check === 'featureDisabled') {
                disabledFeatureNameSet.add(node.featureName);
            } else if (node.check === 'every' || node.check === 'some') {
                for (const c of node.conditions) {
                    walkCondition(c);
                }
            }
        }

        for (const tabConfig of menu) {
            walkCondition(tabConfig.condition);

            for (const subTabConfig of tabConfig.items ?? []) {
                walkCondition(subTabConfig.condition);
            }
        }

        /**
         * Call isAllowed for each found permission. Combine the values into a map on every emit.
         * Many values resolve at the same time, so we use throttleTime(1).
         */
        let isAllowedMap$: Observable<Map<string, boolean>>;

        if (permissionSet.size === 0) {
            isAllowedMap$ = of(new Map());
        } else {
            isAllowedMap$ = combineLatest(
                [...permissionSet].map(p =>
                    this.authorizationService.isAllowed(p).pipe(map(isAllowed => [p, isAllowed] as const)),
                ),
            ).pipe(
                throttleTime(PERMISSIONS_THROTTLE_MS),
                takeUntil(this.destroy$),
                map(entries => new Map(entries)),
            );
        }

        /**
         * Request feature policy statuses at the same time as permissions.
         * While loading, assume no features are enabled.
         */
        const isFeatureEnabledMap$ = this.featurePolicyService
            .getFeatureFlags([...enabledFeatureNameSet, ...disabledFeatureNameSet])
            .pipe(takeUntil(this.destroy$), startWith({}));

        /**
         * Construct a bag of permissions and feature statuses to be used in condition evaluation.
         */
        const bag$: Observable<Bag> = combineLatest({
            isAllowedMap: isAllowedMap$,
            isFeatureEnabledMap: isFeatureEnabledMap$,
        });

        return bag$.pipe(
            switchMap(bag => combineLatest(menu.map(tab => this.makeMainTab$(bag, tab)))),
            map(mainTabs => mainTabs.filter(isTabShown)),
            map(mainTabs => this.filterSeparators(mainTabs)),
            takeUntil(this.destroy$),
            tap(() => {
                this.datadogRumConfigService.measureMenuLoadingTime();
            }),
        );
    }

    makeMainTab$(bag: Bag, tab: TabConfig): Observable<IMainTab | typeof TAB_NOT_SHOWN> {
        return this.makeTab$<IMainTab>(
            bag,
            tab,
            tab.items,
            (base, subTabs, pathProps) => ({
                ...base,
                icon: tab.icon,
                submenu: tab.submenu,
                subTabs,
                ...pathProps,
            }),
            ({ meetsCondition, pathProperties, subTabs }) =>
                !meetsCondition ||
                pathProperties === TAB_NOT_SHOWN ||
                (subTabs.length === 0 && (tab.items?.length ?? 0) > 0 && !tab.options?.showIfEmpty),
        );
    }

    makeSubTab$(bag: Bag, item: SubTabConfig): Observable<ITab | typeof TAB_NOT_SHOWN> {
        return this.makeTab$<ITab>(
            bag,
            item,
            item.subTabs,
            (base, subTabs, pathProps) => {
                const tab: ITab = { ...base, ...pathProps };
                if (subTabs.length > 0) {
                    tab.subTabs = subTabs;
                }
                return tab;
            },
            ({ meetsCondition, pathProperties }) => !meetsCondition || pathProperties === TAB_NOT_SHOWN,
        );
    }

    private makeTab$<T extends ITab | IMainTab>(
        bag: Bag,
        config: TabConfig | SubTabConfig,
        childConfigs: (TabConfig | SubTabConfig)[] | undefined,
        buildTab: (base: any, subTabs: ITab[], pathProps: any) => T,
        shouldHideFn: (args: { meetsCondition: boolean; pathProperties: any; subTabs: ITab[] }) => boolean,
    ): Observable<T | typeof TAB_NOT_SHOWN> {
        const children = childConfigs ?? [];

        const subTabs$ =
            children.length > 0
                ? combineLatest(children.map(child => this.makeSubTab$(bag, child))).pipe(
                      map(subTabs => subTabs.filter(isTabShown)),
                  )
                : of([]);

        return combineLatest({
            meetsCondition: this.evaluateCondition$(bag, config.condition),
            pathProperties: this.evaluatePath$(config.path),
            subTabs: subTabs$,
        }).pipe(
            map(({ meetsCondition, pathProperties, subTabs }) => {
                if (shouldHideFn({ meetsCondition, pathProperties, subTabs })) {
                    return TAB_NOT_SHOWN;
                }

                const base = {
                    title: config.title,
                    navMenuId: config.navMenuId,
                    active: false,
                };

                return buildTab(base, subTabs, pathProperties);
            }),
        );
    }

    evaluateCondition$(bag: Bag, expr?: ConditionExpr): Observable<boolean> {
        if (!expr) {
            return of(true); // default
        } else if (typeof expr === 'function') {
            const result$ = runInInjectionContext(this.environmentInjector, expr);
            return result$.pipe(
                catchError(e => {
                    console.error('Failed to evaluate tab condition:', e);
                    return of(false);
                }),
                startWith(false),
                takeUntil(this.destroy$),
            );
        } else if (expr.check === 'isAllowed') {
            return of(Boolean(bag.isAllowedMap.get(expr['permission'])));
        } else if (expr.check === 'featureEnabled') {
            return of(Boolean(bag.isFeatureEnabledMap[expr['featureName']]?.enabled));
        } else if (expr.check === 'featureDisabled') {
            return of(!Boolean(bag.isFeatureEnabledMap[expr['featureName']]?.enabled));
        } else if (expr.check === 'every') {
            const subConditions = expr['conditions'].map((c: ConditionExpr) => this.evaluateCondition$(bag, c));
            if (subConditions.length === 0) {
                return of(true);
            } else {
                return combineLatest(subConditions).pipe(map((results: boolean[]) => results.every(Boolean)));
            }
        } else if (expr.check === 'some') {
            const subConditions = expr['conditions'].map((c: ConditionExpr) => this.evaluateCondition$(bag, c));
            if (subConditions.length === 0) {
                return of(true);
            } else {
                return combineLatest(subConditions).pipe(map((results: boolean[]) => results.some(Boolean)));
            }
        } else {
            console.error('Unknown condition type:', typeof expr, expr['check']);
            return of(false);
        }
    }

    evaluatePath$(path?: PathExpr): Observable<ITabPath | typeof TAB_NOT_SHOWN> {
        if (!path) {
            return of({});
        } else if (typeof path === 'string') {
            return of({ path });
        } else if (path.type === 'url') {
            return of({ href: path.href, target: '_blank' });
        } else if (path.type === 'app') {
            return this.pure1NavigationService
                .link(path.appId, path.path)
                .pipe(map(link => ({ href: link, target: '_blank' })));
        } else {
            console.error('Unknown path type:', path);
            return of(TAB_NOT_SHOWN);
        }
    }

    /**
     * Removes separators where the next item is not a tab link.
     * This prevents having a separator at the end of the list, and multiple separators in a row.
     */
    private filterSeparators(mainTabs: IMainTab[]): IMainTab[] {
        return mainTabs.filter((curr, idx) => {
            if (curr.title === MENU_SEPARATOR_TITLE) {
                const next = mainTabs[idx + 1];
                if (!next || next.title === MENU_SEPARATOR_TITLE) {
                    return false;
                }
            }
            return true;
        });
    }
}
